/* eslint-disable no-restricted-imports */
import rawContentJson from '../../../public/data/content.json';
import rawLegalJson from '../../../public/data/legal.json';
import rawOpenTimingJson from '../../../public/data/openTiming.json';
const rawContent = rawContentJson as Record<string, unknown>;
const rawOpenTiming = rawOpenTimingJson as Record<string, unknown>;
interface LegalEntry {
    title: string;
    content: string;
}
interface LegalData {
    page_labels: LegalPageLabels;
    [key: string]: LegalEntry | LegalPageLabels;
}
const rawLegal = rawLegalJson as unknown as LegalData;

export interface WebsiteSettings {
    restaurant_name: string;
    restaurant_email: string;
    restaurant_phonenumber: string;
    restaurant_whatsapp: string;
    restaurant_address: string;
    restaurant_city: string;
    restaurant_postal_code: string;
    restaurant_country: string;
    social_facebook: string;
    social_instagram: string;
    social_tiktok: string;
    social_tripadvisor: string;
    social_whatsapp_channel: string;
    pickup_information: string;
    delivery_information: string;
    contact_map_embed: string;
}

export interface PageSeo {
    slug: string;
    name: string;
    url: string;
    meta_title: string;
    meta_description: string;
    meta_keywords: string;
    robots: string;
    og_title: string;
    og_description: string;
    og_type: string;
    h1: string;
    h2?: string;
}

export interface Service {
    icon: string;
    title: string;
    subtitle?: string;
    description: string;
    sort_order?: number;
    cta?: { label: string; to: string } | null;
}

export interface Quality {
    icon: string;
    title: string;
    description: string;
    sort_order: number;
}

interface AboutSection {
    title: string;
    items: string[];
}

export interface AboutData {
    body: string;
    values: string[];
    philosophy: AboutSection;
    philosophy_heading: string;
    zero_waste_card: { title: string; description: string };
    stat: { value: string; label: string };
    images: { src: string; alt: string }[];
    differentiators: AboutSection;
    story_cta: string;
    page_heading: string;
    hero_image?: string;
    hero_badge?: string;
    values_label: string;
}

interface HeroTrustBadge {
    emoji: string;
    title: string;
    subtitle: string;
    link?: string;
}

export interface HeroData {
    badge: string;
    heading: string;
    heading_highlight: string;
    tagline: string;
    cta_primary: string;
    cta_secondary: string;
    trust_badges: HeroTrustBadge[];
    floating_badge: { title: string; subtitle: string };
    floating_badges: HeroTrustBadge[];
    image: string;
    image_alt: string;
}

export interface SpecialtiesSection {
    badge: string;
    heading: string;
    description: string;
    cta: string;
    favorite_label: string;
    featured_label: string;
    add_to_cart_label: string;
    food_image_fallback: string;
    categories_heading: string;
    categories_count_suffix: string;
    footer_message: string;
    view_details: string;
    default_food_description: string;
}

export interface ServicesPageData {
    mobile_title: string;
    mobile_subtitle: string;
    desktop_title: string;
    desktop_subtitle: string;
    contact_button: string;
}

export interface OpenTiming {
    day_of_week: number;
    meal_type: string;
    is_pick: boolean;
    is_delivery: boolean;
}

export interface MarketDetail {
    id: number;
    name: string;
    address: string;
    phone: string;
    email: string;
}

export interface MarketTiming {
    date: string;
    period: string;
    start_time: string;
    end_time: string;
    contactId: number;
}

export interface Features {
    login: boolean;
    order: boolean;
    reviews: boolean;
    delivery: boolean;
}

interface MobileNavItem {
    href: string;
    label: string;
    emoji: string;
    requiresAuth?: boolean;
}

interface MobileNavCart {
    label: string;
    emoji: string;
}

export interface MobileNav {
    items: MobileNavItem[];
    cart: MobileNavCart;
}

interface ContactInfo {
    form_title: string;
    default_section_title: string;
    page_title: string;
    page_subtitle: string;
    label_phone: string;
    label_email: string;
    label_whatsapp: string;
    label_address: string;
    social_heading: string;
    holidays_heading: string;
    instagram_label?: string;
    facebook_label?: string;
}

interface ContactFormLabels {
    subject: string;
    name: string;
    email: string;
    phone: string;
    message: string;
    submit: string;
    submitting: string;
}

interface ContactFormPlaceholders {
    name: string;
    email: string;
    phone: string;
    message: string;
}

interface ContactFormErrors {
    name_required: string;
    email_invalid: string;
    message_required: string;
}

export interface Contact {
    slug_to_subject: Record<string, string>;
    subjects: string[];
    default_subject: string;
    info: ContactInfo;
    form_labels: ContactFormLabels;
    form_placeholders: ContactFormPlaceholders;
    form_errors: ContactFormErrors;
    success_message: string;
    error_message: string;
    recaptcha_error: string;
}

export interface Brand {
    logo: string;
    day_short: string[];
    restaurant_name?: string;
}

export interface HeaderData {
    menu_label: string;
    order_label?: string;
    about_label: string;
    services_label: string;
    contact_label: string;
    open_label: string;
    until_label: string;
    opens_at_label: string;
    home_label: string;
    call_label: string;
    profile_label: string;
    login_label: string;
    close_menu_label: string;
    open_menu_label: string;
}

export interface FooterData {
    description: string;
    quick_links_heading: string;
    legal_heading: string;
    contact_heading: string;
    unavailable_timings: string;
    order_only: string;
    rights_reserved: string;
    legal_info_label: string;
    cgv_label: string;
    privacy_label: string;
    cookies_label: string;
    days: string[];
}

export interface QualitySectionData {
    badge: string;
    heading: string;
}

export interface EventCateringData {
    badge: string;
    heading: string;
    description: string;
    cta_quote: string;
    cta_order: string;
}

export interface SocialProofData {
    heading: string;
    description: string;
    human_heading: string;
    ai_heading: string;
    human_links: {
        label: string;
        href: string;
        icon: 'google' | 'facebook';
    }[];
    ai_links: {
        label: string;
        href: string;
        icon: 'chatgpt' | 'gemini';
    }[];
}

export interface JournalEntry {
    title: string;
    excerpt: string;
    content: string;
    image_url: string;
    link: string;
    author: string;
    published_at: string;
}

export interface ErrorPages {
    not_found_title: string;
    not_found_description: string;
    back_home: string;
    view_menu: string;
}

export interface LegalPageLabels {
    back_button: string;
    loading: string;
    content_unavailable: string;
    invalid_page: string;
}

export const legalPage = rawLegal.page_labels;

export interface CartLabels {
    title: string;
    empty_message: string;
    view_menu: string;
    total: string;
    checkout: string;
    close_label: string;
    remove_label: string;
    add_label: string;
    adding_label: string;
    popular_badge: string;
    house_made_badge: string;
    per_portion: string;
    reviews_label: string;
    description_tab: string;
    ingredients_tab: string;
    allergens_tab: string;
    no_ingredients: string;
    no_allergens: string;
    allergen_prefix: string;
    contains_prefix: string;
    promo_code_error: string;
    add_button: string;
    qty_label: string;
    capacity_warning: string;
    group_date_prefix: string;
    group_no_date: string;
    menu_button: string;
    modal_title: string;
    modal_period_label: string;
    modal_fulfilment_label: string;
    modal_confirm: string;
    modal_another_order_warning: string;
    diff_slot_title: string;
    diff_slot_message: string;
    diff_slot_confirm: string;
    diff_slot_cancel: string;
    weight_cap_title: string;
    weight_cap_message: string;
    weight_cap_action: string;
    weight_cap_close: string;
    weight_cap_slot_full: string;
    weight_cap_slot_full_cta: string;
    unavailable_title: string;
    unavailable_message: string;
    unavailable_other_items_heading: string;
    unavailable_slots_heading: string;
    period_lunch_label: string;
    period_dinner_label: string;
    fulfilment_pickup_label: string;
    fulfilment_delivery_label: string;
    period_label: string;
    fulfilment_label: string;
    submit_label: string;
    submitting_label: string;
    page_title: string;
    contact_section: string;
    name_label: string;
    name_placeholder: string;
    email_label: string;
    email_placeholder: string;
    phone_label: string;
    phone_placeholder: string;
    address_label: string;
    address_placeholder: string;
    payment_section: string;
    payment_online: string;
    payment_online_desc: string;
    payment_cb_delivery: string;
    payment_cb_delivery_desc: string;
    payment_cash_delivery: string;
    payment_cash_delivery_desc: string;
    summary_section: string;
    empty_cart: string;
    total_label: string;
    time_label: string;
    time_placeholder: string;
    time_unavailable: string;
    error_time: string;
    error_generic: string;
    error_name: string;
    error_email: string;
    error_phone: string;
    error_address: string;
    success_title: string;
    success_single: string;
    success_multiple: string;
    success_back: string;
    session_not_found: string;
    verify_error_fallback: string;
    verifying_payment: string;
    payment_not_confirmed: string;
    back_to_checkout: string;
    payment_accepted: string;
    select_date_label: string;
    item_unavailable_warning: string;
    item_unavailable_remove: string;
    validation_error_label: string;
    decrease_qty_label: string;
    increase_qty_label: string;
    article_label: string;
    unit_price_label: string;
    product_fallback_label: string;
}

export interface ApiErrorsData {
    network_error: string;
    unknown_error: string;
    default_fallback: string;
}

export interface SectionApiData {
    default_description: string;
}

export interface TemplateData {
    view_video: string;
    find_us: string;
    coords_missing: string;
    section_unsupported: string;
    back_home: string;
    not_found_subtitle: string;
    not_found_message: string;
    close_label: string;
}

export interface SectionErrors {
    header: string;
    footer: string;
    menu: string;
    hero: string;
    contact: string;
    about: string;
    services: string;
    comments: string;
    top_sellers: string;
    delivery_rules: string;
    page_content: string;
    page_structure: string;
    store_settings: string;
}

export interface CookiesData {
    title: string;
    description: string;
    policy_label: string;
    accept_label: string;
    reject_label: string;
    aria_label: string;
}

export interface PWAData {
    title: string;
    description: string;
    install_label: string;
    later_label: string;
}

export interface MenuPageData {
    badge?: string;
    description?: string;
    page_title: string;
    retry_button: string;
    no_products: string;
    no_products_category: string;
    no_products_category_desc: string;
    loading_details: string;
    load_error: string;
    back_to_menu: string;
    not_found: string;
    coming_soon: string;
    filter_label: string;
    close_label: string;
    available_from: string;
    available_on: string;
    available_today: string;
    available_tomorrow: string;
    portions_available: string;
    out_of_stock: string;
    filter_date_label: string;
    filter_date_placeholder: string;
    filter_period_label: string;
    filter_period_all: string;
    filter_period_lunch: string;
    filter_period_dinner: string;
    filter_reset: string;
    filter_category_label: string;
    filter_apply: string;
    filter_today: string;
    filter_pickup: string;
    filter_delivery: string;
    filter_delivery_soon: string;
    filter_all_categories: string;
    filter_in_stock: string;
    filter_include_oos: string;
    filter_indisponible: string;
    filter_drawer_title: string;
    filter_button_label: string;
    filter_parameters_heading: string;
    filter_results_count: string;
    filter_stock_active_label: string;
    filter_search_placeholder: string;
    view_grid: string;
    view_list: string;
    no_foods_for_filter: string;
    slot_remaining_weight: string;
    tab_filters: string;
    tab_parameters: string;
    tab_information: string;
    info_how_to_order_title: string;
    info_how_to_order_body: string;
    info_availability_title: string;
    info_availability_body: string;
    info_pickup_delivery_title: string;
    info_pickup_delivery_body: string;
    info_filters_title: string;
    info_filters_body: string;
    menu_section_heading: string;
    menu_select_items: string;
    menu_selection_warning: string;
    menu_discount_label: string;
    menu_weight_label: string;
    menu_savings_label: string;
    menu_unavailable: string;
    menu_not_found: string;
    pick_one_label: string;
    pick_multiple_label: string;
    option_label: string;
    options_label: string;
    selection_recap_heading: string;
    group_progress_label: string;
    availability_remaining_label: string;
    availability_full_label: string;
    top_sellers_heading: string;
    food_seo_description_fallback: string;
    create_menu_title: string;
    create_menu_subtitle: string;
    create_menu_name_label: string;
    create_menu_name_placeholder: string;
    create_menu_price_label: string;
    create_menu_price_placeholder: string;
    create_menu_description_label: string;
    create_menu_description_placeholder: string;
    create_menu_select_foods: string;
    create_menu_select_hint: string;
    create_menu_error_name: string;
    create_menu_error_price: string;
    create_menu_error_foods: string;
    create_menu_success: string;
    create_menu_details_heading: string;
    create_menu_total_value: string;
    create_menu_submitting: string;
    create_menu_submit: string;
}

export const websiteSettings = rawContent.website_settings as unknown as WebsiteSettings;
export const pageSeo = rawContent.page_seo as unknown as PageSeo[];
export const quality = rawContent.quality as unknown as Quality[];
export const services = rawContent.services as unknown as Service[];
export const about = rawContent.about as unknown as AboutData;
export const features = rawContent.features as unknown as Features;
export const mobileNav = rawContent.mobile_nav as unknown as MobileNav;
export const contact = rawContent.contact as unknown as Contact;
export const brand = rawContent.brand as unknown as Brand;
export const openTimings = rawOpenTiming.open_timings as unknown as OpenTiming[];
export const defaultPickupAddress = rawOpenTiming.default_pickup_address as string;
export const marketDetails = rawOpenTiming.marketDetails as unknown as MarketDetail[];
export const marketTiming = rawOpenTiming.marketTiming as unknown as MarketTiming[];

export const hero = rawContent.hero as unknown as HeroData;
export const specialtiesSection = rawContent.specialties_section as unknown as SpecialtiesSection;
export const servicesPage = rawContent.services_page as unknown as ServicesPageData;
export const header = rawContent.header as unknown as HeaderData;
export const footer = rawContent.footer as unknown as FooterData;
export const qualitySection = rawContent.quality_section as unknown as QualitySectionData;
export const eventCatering = rawContent.event_catering as unknown as EventCateringData;
export const socialProof = rawContent.social_proof as unknown as SocialProofData;
export const journals = (rawContent.journals ?? []) as unknown as JournalEntry[];
export const menuPage = rawContent.menu as unknown as MenuPageData;
export const errorPages = rawContent.error_pages as unknown as ErrorPages;
export const cartLabels = rawContent.cart as unknown as CartLabels;
export const pwa = rawContent.pwa as unknown as PWAData;
export const cookies = rawContent.cookies as unknown as CookiesData;
export const apiErrors = rawContent.api_errors as unknown as ApiErrorsData;
export const templateData = rawContent.template as unknown as TemplateData;
export const sectionApiData = rawContent.section_api as unknown as SectionApiData;
export const sectionErrors = rawContent.section_errors as unknown as SectionErrors;
export const legal = rawLegal as unknown as Record<string, LegalEntry>;

export const getServicePageServicesWithCta = (): Service[] => services;

export const getPageSeo = (slug: string): PageSeo | undefined => pageSeo.find((p) => p.slug === slug);

export default rawContent;
